import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, History, Info, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { botsApi } from '@/api/bots';
import { unwrapError } from '@/api/client';
import { promptVersionsApi } from '@/api/prompt-versions';
import type { Bot, PromptVersion, PromptVersionSummary } from '@/api/types';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { diffLines, type DiffLine } from '@/lib/line-diff';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadMore } from '@/components/data/LoadMore';

type CompareMode = 'active' | 'previous';

const CONTEXT_LINES = 3;

export function PromptHistoryDialog({
  bot,
  open,
  onOpenChange,
  canManage,
}: {
  bot: Bot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setBot } = useWorkspace();
  const [selected, setSelected] = React.useState<number | null>(null);
  const [compareMode, setCompareMode] = React.useState<CompareMode>('active');
  const [restoreOpen, setRestoreOpen] = React.useState(false);

  const versions = useInfiniteQuery({
    queryKey: ['prompt-versions', bot.botId, 'history'],
    queryFn: ({ pageParam }) =>
      promptVersionsApi.list(bot.botId, { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled: open,
  });
  const models = useQuery({
    queryKey: ['org-llm-models'],
    queryFn: () => botsApi.models(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const items = versions.data?.pages.flatMap((p) => p.items) ?? [];
  const activeVersion = versions.data?.pages[0]?.activeVersion ?? null;
  const latestVersion = items[0]?.version ?? null;
  const current = selected ?? activeVersion ?? latestVersion;
  const currentSummary = items.find((v) => v.version === current) ?? null;
  const isActive = current !== null && current === activeVersion;
  const effectiveMode: CompareMode = isActive || activeVersion === null ? 'previous' : compareMode;
  const baseVersion =
    current === null ? null : effectiveMode === 'active' ? activeVersion : current > 1 ? current - 1 : null;

  const detail = usePromptVersion(bot.botId, current, open);
  const base = usePromptVersion(bot.botId, baseVersion, open);
  const defaultModel = models.data?.default.model;

  const select = (version: number): void => {
    setSelected(version);
    setCompareMode('active');
  };

  const test = (): void => {
    if (current === null) return;
    setBot(bot.botId);
    onOpenChange(false);
    navigate(`/chat?promptVersion=${current}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-6xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            {t('promptVersions.title', { name: bot.name })}
          </DialogTitle>
          <DialogDescription>{t('promptVersions.subtitle')}</DialogDescription>
        </DialogHeader>

        {versions.isPending ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : versions.isError ? (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-destructive">
            {unwrapError(versions.error)}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
            <History className="h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium">{t('promptVersions.empty.title')}</p>
            <p className="max-w-md text-sm text-muted-foreground">{t('promptVersions.empty.body')}</p>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[300px_1fr]">
            <div className="min-h-0 overflow-y-auto border-b md:border-b-0 md:border-r">
              {items.map((v) => (
                <VersionListItem
                  key={v.version}
                  version={v}
                  active={v.version === activeVersion}
                  selected={v.version === current}
                  onSelect={() => select(v.version)}
                />
              ))}
              <LoadMore
                hasNext={!!versions.hasNextPage}
                loading={versions.isFetchingNextPage}
                onClick={() => void versions.fetchNextPage()}
              />
            </div>

            <div className="min-h-0 overflow-y-auto p-6">
              {currentSummary ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-mono text-xl font-semibold">v{currentSummary.version}</h3>
                        {isActive ? (
                          <Badge variant="success">{t('promptVersions.active')}</Badge>
                        ) : null}
                        <SourceBadge version={currentSummary} />
                      </div>
                      <p className="text-sm">{currentSummary.note ?? defaultTitle(currentSummary, t)}</p>
                      <p className="text-xs text-muted-foreground">
                        {[currentSummary.author, formatFullDate(currentSummary.createdAt, i18n.language)]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={test}>
                        <FlaskConical className="h-4 w-4" />
                        {t('promptVersions.test')}
                      </Button>
                      <Button
                        size="sm"
                        className="gap-1.5"
                        disabled={isActive || !canManage}
                        title={!canManage ? t('promptVersions.restoreRestricted') : undefined}
                        onClick={() => setRestoreOpen(true)}
                      >
                        <RotateCcw className="h-4 w-4" />
                        {t('promptVersions.restore')}
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-3">
                    <Fact
                      label={t('promptVersions.fields.llmModel')}
                      value={modelLabel(currentSummary.llmModel, defaultModel, t)}
                      mono
                    />
                    <Fact
                      label={t('promptVersions.prompt')}
                      value={
                        currentSummary.promptLength > 0
                          ? t('promptVersions.chars', { count: currentSummary.promptLength })
                          : t('promptVersions.defaultPrompt')
                      }
                    />
                    <Fact
                      label={t('promptVersions.changes')}
                      value={
                        currentSummary.changedFields.length > 0
                          ? currentSummary.changedFields.map((f) => t(`promptVersions.fields.${f}`)).join(' · ')
                          : '—'
                      }
                    />
                  </div>

                  <Tabs defaultValue="diff">
                    <TabsList>
                      <TabsTrigger value="diff">{t('promptVersions.tabs.diff')}</TabsTrigger>
                      <TabsTrigger value="prompt">{t('promptVersions.tabs.prompt')}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="diff" className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-muted-foreground">{t('promptVersions.compareWith')}</span>
                        <Select value={effectiveMode} onValueChange={(v: CompareMode) => setCompareMode(v)}>
                          <SelectTrigger className="h-8 w-auto min-w-[220px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active" disabled={isActive || activeVersion === null}>
                              {t('promptVersions.compare.active', { version: activeVersion ?? '—' })}
                            </SelectItem>
                            <SelectItem value="previous" disabled={currentSummary.version <= 1}>
                              {t('promptVersions.compare.previous', {
                                version: currentSummary.version > 1 ? currentSummary.version - 1 : '—',
                              })}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {baseVersion === null ? (
                        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                          {t('promptVersions.noBase')}
                        </p>
                      ) : detail.data && base.data ? (
                        <VersionComparison
                          base={base.data}
                          target={detail.data}
                          defaultModel={defaultModel}
                        />
                      ) : detail.isError || base.isError ? (
                        <p className="text-sm text-destructive">{unwrapError(detail.error ?? base.error)}</p>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('common.loading')}
                        </div>
                      )}
                    </TabsContent>
                    <TabsContent value="prompt">
                      {detail.data ? (
                        detail.data.systemPrompt ? (
                          <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-lg border bg-muted/30 p-4 font-mono text-xs leading-relaxed">
                            {detail.data.systemPrompt}
                          </pre>
                        ) : (
                          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                            {t('promptVersions.defaultPromptHelp')}
                          </p>
                        )
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('common.loading')}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {restoreOpen && current !== null && activeVersion !== null && latestVersion !== null ? (
          <RestoreVersionDialog
            bot={bot}
            version={current}
            activeVersion={activeVersion}
            nextVersion={latestVersion + 1}
            defaultModel={defaultModel}
            onOpenChange={setRestoreOpen}
            onRestored={(restored) => {
              if (restored.promptVersion) setSelected(restored.promptVersion);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function usePromptVersion(botId: string, version: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['prompt-version', botId, version],
    queryFn: () => promptVersionsApi.get(botId, version!),
    enabled: enabled && version !== null,
    staleTime: Infinity,
  });
}

function VersionListItem({
  version,
  active,
  selected,
  onSelect,
}: {
  version: PromptVersionSummary;
  active: boolean;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative w-full border-b px-4 py-3 text-left transition-colors',
        selected ? 'bg-primary/5' : 'hover:bg-muted/50',
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-0 h-full w-0.5 bg-primary transition-opacity',
          selected ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold">v{version.version}</span>
        {active ? (
          <Badge variant="success" className="px-2 py-0 text-[10px]">
            {t('promptVersions.active')}
          </Badge>
        ) : null}
        {version.source === 'restore' ? (
          <Badge variant="info" className="gap-1 px-2 py-0 text-[10px]">
            <RotateCcw className="h-3 w-3" />v{version.restoredFrom}
          </Badge>
        ) : null}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {timeAgo(version.createdAt, i18n.language)}
        </span>
      </div>
      <p className={cn('mt-1 line-clamp-2 text-sm', !version.note && 'text-muted-foreground')}>
        {version.note ?? defaultTitle(version, t)}
      </p>
      {version.changedFields.length > 0 ? (
        <p className="mt-1 truncate text-[11px] text-muted-foreground">
          {version.changedFields.map((f) => t(`promptVersions.fields.${f}`)).join(' · ')}
        </p>
      ) : null}
      {version.author ? (
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground/80">{version.author}</p>
      ) : null}
    </button>
  );
}

function SourceBadge({ version }: { version: PromptVersionSummary }): JSX.Element | null {
  const { t } = useTranslation();
  if (version.source === 'restore') {
    return (
      <Badge variant="info" className="gap-1">
        <RotateCcw className="h-3 w-3" />
        {t('promptVersions.restoredFrom', { version: version.restoredFrom })}
      </Badge>
    );
  }
  if (version.source === 'import') {
    return <Badge variant="outline">{t('promptVersions.source.import')}</Badge>;
  }
  return null;
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('truncate text-sm', mono && 'font-mono text-xs')} title={value}>
        {value}
      </div>
    </div>
  );
}

function VersionComparison({
  base,
  target,
  defaultModel,
}: {
  base: PromptVersion;
  target: PromptVersion;
  defaultModel: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const lines = React.useMemo(
    () => diffLines(base.systemPrompt ?? '', target.systemPrompt ?? ''),
    [base.systemPrompt, target.systemPrompt],
  );
  const added = lines.filter((l) => l.kind === 'add').length;
  const removed = lines.filter((l) => l.kind === 'remove').length;
  const modelChanged = (base.llmModel ?? '') !== (target.llmModel ?? '');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-mono text-xs text-muted-foreground">
          v{base.version} → v{target.version}
        </span>
        {added + removed > 0 ? (
          <span className="text-xs">
            <span className="font-medium text-emerald-600 dark:text-emerald-400">+{added}</span>{' '}
            <span className="font-medium text-red-600 dark:text-red-400">−{removed}</span>{' '}
            <span className="text-muted-foreground">{t('promptVersions.lines')}</span>
          </span>
        ) : null}
      </div>

      {modelChanged ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-amber-500/5 px-3 py-2 text-sm">
          <span className="font-medium">{t('promptVersions.fields.llmModel')}</span>
          <code className="font-mono text-xs text-red-600 line-through dark:text-red-400">
            {modelLabel(base.llmModel, defaultModel, t)}
          </code>
          <span className="text-muted-foreground">→</span>
          <code className="font-mono text-xs text-emerald-700 dark:text-emerald-400">
            {modelLabel(target.llmModel, defaultModel, t)}
          </code>
        </div>
      ) : null}

      {Boolean(base.systemPrompt) !== Boolean(target.systemPrompt) ? (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('promptVersions.defaultSide', {
            version: base.systemPrompt ? target.version : base.version,
          })}
        </p>
      ) : null}

      {!base.systemPrompt && !target.systemPrompt ? (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {t('promptVersions.bothDefault')}
        </p>
      ) : added + removed === 0 ? (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {t('promptVersions.noPromptChange')}
        </p>
      ) : (
        <DiffView lines={lines} />
      )}
    </div>
  );
}

type DiffBlock = { kind: 'lines'; lines: DiffLine[] } | { kind: 'fold'; lines: DiffLine[] };

function foldUnchanged(lines: DiffLine[]): DiffBlock[] {
  const blocks: DiffBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].kind !== 'same') {
      const start = i;
      while (i < lines.length && lines[i].kind !== 'same') i++;
      blocks.push({ kind: 'lines', lines: lines.slice(start, i) });
      continue;
    }
    const start = i;
    while (i < lines.length && lines[i].kind === 'same') i++;
    const run = lines.slice(start, i);
    const head = start === 0 ? 0 : CONTEXT_LINES;
    const tail = i === lines.length ? 0 : CONTEXT_LINES;
    if (run.length <= head + tail + 2) {
      blocks.push({ kind: 'lines', lines: run });
    } else {
      blocks.push({ kind: 'lines', lines: run.slice(0, head) });
      blocks.push({ kind: 'fold', lines: run.slice(head, run.length - tail) });
      blocks.push({ kind: 'lines', lines: run.slice(run.length - tail) });
    }
  }
  return blocks;
}

function DiffView({ lines }: { lines: DiffLine[] }): JSX.Element {
  const { t } = useTranslation();
  const blocks = React.useMemo(() => foldUnchanged(lines), [lines]);
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());

  return (
    <div className="overflow-hidden rounded-lg border font-mono text-xs leading-5">
      {blocks.map((block, idx) =>
        block.kind === 'fold' && !expanded.has(idx) ? (
          <button
            key={idx}
            type="button"
            onClick={() => setExpanded((prev) => new Set(prev).add(idx))}
            className="block w-full border-y bg-muted/40 px-3 py-1 text-left font-sans text-[11px] text-muted-foreground hover:bg-muted"
          >
            {t('promptVersions.unchangedLines', { count: block.lines.length })}
          </button>
        ) : (
          block.lines.map((line, lineIdx) => (
            <div
              key={`${idx}-${lineIdx}`}
              className={cn(
                'flex',
                line.kind === 'add' && 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-200',
                line.kind === 'remove' && 'bg-red-500/10 text-red-900 dark:text-red-200',
              )}
            >
              <span className="w-6 shrink-0 select-none text-center text-muted-foreground">
                {line.kind === 'add' ? '+' : line.kind === 'remove' ? '−' : ''}
              </span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words pr-3">{line.text || ' '}</span>
            </div>
          ))
        ),
      )}
    </div>
  );
}

function RestoreVersionDialog({
  bot,
  version,
  activeVersion,
  nextVersion,
  defaultModel,
  onOpenChange,
  onRestored,
}: {
  bot: Bot;
  version: number;
  activeVersion: number;
  nextVersion: number;
  defaultModel: string | undefined;
  onOpenChange: (open: boolean) => void;
  onRestored: (bot: Bot) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refreshBots, currentOrgId } = useWorkspace();
  const [note, setNote] = React.useState('');
  const target = usePromptVersion(bot.botId, version, true);
  const active = usePromptVersion(bot.botId, activeVersion, true);

  const promptDiff = React.useMemo(() => {
    if (!target.data || !active.data) return null;
    const lines = diffLines(active.data.systemPrompt ?? '', target.data.systemPrompt ?? '');
    return {
      added: lines.filter((l) => l.kind === 'add').length,
      removed: lines.filter((l) => l.kind === 'remove').length,
    };
  }, [target.data, active.data]);

  const mutation = useMutation({
    mutationFn: () => promptVersionsApi.restore(bot.botId, version, note.trim() || undefined),
    onSuccess: (restored) => {
      toast.success(t('promptVersions.restoredToast', { from: version, version: restored.promptVersion }));
      void queryClient.invalidateQueries({ queryKey: ['prompt-versions', bot.botId] });
      void queryClient.invalidateQueries({ queryKey: ['org', 'bots', 'list', currentOrgId] });
      void queryClient.invalidateQueries({ queryKey: ['system'] });
      refreshBots();
      onRestored(restored);
      onOpenChange(false);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('promptVersions.restoreDialog.title', { version })}</DialogTitle>
            <DialogDescription>
              {t('promptVersions.restoreDialog.subtitle', { version, name: bot.name })}
            </DialogDescription>
          </DialogHeader>

          <dl className="divide-y rounded-lg border text-sm">
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <dt className="text-muted-foreground">{t('promptVersions.prompt')}</dt>
              <dd className="text-right">
                {!promptDiff ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                ) : promptDiff.added + promptDiff.removed === 0 ? (
                  t('promptVersions.restoreDialog.promptSame')
                ) : (
                  <span className="font-mono text-xs">
                    <span className="text-emerald-600 dark:text-emerald-400">+{promptDiff.added}</span>{' '}
                    <span className="text-red-600 dark:text-red-400">−{promptDiff.removed}</span>{' '}
                    <span className="font-sans text-muted-foreground">{t('promptVersions.lines')}</span>
                  </span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <dt className="text-muted-foreground">{t('promptVersions.fields.llmModel')}</dt>
              <dd className="truncate text-right font-mono text-xs">
                {target.data && active.data
                  ? (active.data.llmModel ?? '') === (target.data.llmModel ?? '')
                    ? modelLabel(target.data.llmModel, defaultModel, t)
                    : `${modelLabel(active.data.llmModel, defaultModel, t)} → ${modelLabel(target.data.llmModel, defaultModel, t)}`
                  : '…'}
              </dd>
            </div>
          </dl>

          <div className="flex gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>{t('promptVersions.restoreDialog.effect', { next: nextVersion })}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="restore-note">{t('promptVersions.noteLabel')}</Label>
            <Input
              id="restore-note"
              value={note}
              maxLength={280}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('promptVersions.restoreDialog.notePlaceholder', { version: activeVersion })}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending} className="gap-1.5">
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              {t('promptVersions.restoreDialog.confirm', { version })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function defaultTitle(version: PromptVersionSummary, t: TFunction): string {
  if (version.source === 'restore') return t('promptVersions.source.restore', { version: version.restoredFrom });
  return t(`promptVersions.source.${version.source}`);
}

function modelLabel(model: string | undefined, defaultModel: string | undefined, t: TFunction): string {
  return model ?? t('promptVersions.defaultModel', { model: defaultModel ?? '…' });
}

function formatFullDate(ms: number, language: string): string {
  return new Date(ms).toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
}

function timeAgo(ms: number, language: string): string {
  const seconds = Math.round((ms - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 30 * 86400) return rtf.format(Math.round(seconds / 86400), 'day');
  return new Date(ms).toLocaleDateString(language, { dateStyle: 'medium' });
}
